import React, { JSX, useState } from 'react'
import toast from 'react-hot-toast'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { isTokenExpired } from '../libs/tokenUtils'

interface LoginResponse {
  user: {
    id: string
    email: string
    fullName: string
    roleSlug: string
  }
  token: string
  expiresAt: string
}

interface ErrorResponse {
  message: string
  rule: string
  field: string
}

const date = new Date()
const random = (Math.floor(date.getTime() / 1000) % 17) + 1

export default function Login(): JSX.Element {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  
  const navigate = useNavigate()
  const location = useLocation()

  // Retrieve the previous page path if provided in route state
  const from = location.state?.from?.pathname || null

  const token = localStorage.getItem('token')

  // Auto-redirect if already logged in
  if (token && !isTokenExpired()) {
    const roleSlug = localStorage.getItem('user_role_slug')
    if (roleSlug === 'user' && from) {
      return <Navigate to={from} replace />
    }
    return <Navigate to="/dashboard" replace />
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      if (!res.ok) {
        const data = await res.json()
        if (data.length > 0) {
          data.forEach((item: ErrorResponse) => {
            toast.error(item.message)
          })
        } else {
          toast.error(data.errors?.[0]?.message || 'Gagal login')
        }
        return
      }

      const data: LoginResponse = await res.json()

      localStorage.setItem('token', data.token)
      localStorage.setItem('token_expires_at', data.expiresAt)
      localStorage.setItem('user', data.user.fullName)
      localStorage.setItem('user_id', data.user.id)
      localStorage.setItem('user_email', data.user.email)
      localStorage.setItem('user_role_slug', data.user.roleSlug)

      toast.success('Berhasil login')

      // Redirect logic based on role
      if (data.user.roleSlug === 'user') {
        if (from) {
          navigate(from, { replace: true })
        } else {
          // If no state exists, go back in history or fallback to dataset list
          navigate(-1)
        }
      } else {
        navigate('/dashboard', { replace: true })
      }
    } catch (err) {
      console.error(err)
      toast.error('Terjadi kesalahan koneksi server')
    }
  }

  const backgroundURL = `https://satudata.karanganyarkab.go.id//assets/img/background/karanganyar-${random}.jpg`

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{ backgroundImage: `url(${backgroundURL})` }}
    >
      <div className="absolute inset-0 bg-black/70"></div>

      <div className="flex flex-col gap-6 items-center z-10">
        <button onClick={() => navigate('/')}>
          <div className="relative flex items-center gap-8">
            <img src="/logo.png" alt="logo" className="size-20" />
            <p className="text-3xl font-bold text-white">Profil Daerah Karanganyar</p>
          </div>
        </button>

        <form onSubmit={handleLogin} className="relative bg-white p-6 rounded shadow-md w-80 space-y-4">
          <h2 className="text-2xl font-bold text-center">Login</h2>
          <input
            type="email"
            placeholder="Email"
            className="w-full border px-3 py-2 rounded"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            className="w-full border px-3 py-2 rounded"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="submit"
            className="w-full bg-primary-600 text-white py-2 rounded hover:bg-primary-700 font-semibold"
          >
            Login
          </button>
        </form>
      </div>
    </div>
  )
}
